import React from 'react';
import { useInView } from '../hooks/useInView';

const cards = [
  {
    icon: '◎',
    title: 'Exceptions Only',
    desc: 'A deterministic engine surfaces just what needs a human — no re-reading conforming records.',
    color: '#00C9B1',
  },
  {
    icon: '⚭',
    title: 'Server-Enforced Trust',
    desc: 'Every step role-bound, e-signed, audited, and site-scoped. Tamper-evident by design.',
    color: '#7B3FE4',
  },
  {
    icon: '⚡',
    title: 'Faster Release',
    desc: 'Review-by-exception cuts review hours and compresses time-to-release across the plant.',
    color: '#F5A623',
  },
  {
    icon: '∞',
    title: 'Bends to You',
    desc: 'Workflow, thresholds, roles, and rules are configuration — shaped without a development cycle.',
    color: '#00C9B1',
    badge: 'Configurable',
  },
];

function Card({ card, delay }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 16,
      padding: '28px 28px 32px',
      position: 'relative',
      overflow: 'hidden',
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
    }}
    onMouseEnter={e => {
      e.currentTarget.style.border = `1px solid ${card.color}30`;
      e.currentTarget.style.background = `${card.color}08`;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.border = '1px solid rgba(255,255,255,0.07)';
      e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
    }}>
      {/* Top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, ${card.color}, transparent)`,
      }} />

      {/* Icon */}
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: `${card.color}18`,
        border: `1px solid ${card.color}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20, marginBottom: 18, color: card.color,
      }}>{card.icon}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{card.title}</h3>
        {card.badge && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
            background: '#F5A623', color: '#000',
            padding: '2px 8px', borderRadius: 100, textTransform: 'uppercase',
          }}>{card.badge}</span>
        )}
      </div>
      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{card.desc}</p>
    </div>
  );
}

export default function WhyQualicore() {
  const [ref, inView] = useInView();
  return (
    <section id="why" style={{ padding: '120px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <div ref={ref} style={{
        textAlign: 'center', marginBottom: 72,
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
      }}>
        <p style={{
          fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
          color: '#00C9B1', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
        }}>Why Qualicore</p>
        <h2 style={{
          fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 900, lineHeight: 1.08,
          letterSpacing: '-0.03em', color: '#fff', marginBottom: 20,
        }}>Not a product. A platform.</h2>
        <p style={{
          fontSize: 18, color: 'rgba(255,255,255,0.5)', maxWidth: 520, margin: '0 auto',
          lineHeight: 1.7,
        }}>
          100% manual review is slow, costly, and doesn't scale. Review-by-exception does — on a platform that bends to you.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 20,
      }}>
        {cards.map((card, i) => <Card key={card.title} card={card} delay={i * 100} />)}
      </div>

      {/* Bottom banner */}
      <div style={{
        marginTop: 48,
        background: 'linear-gradient(135deg, rgba(0,201,177,0.08), rgba(123,63,228,0.08))',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 16, padding: '20px 28px',
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#00C9B1', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>One Platform</span>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
          One governed core, four pillars — <span style={{ color: '#fff' }}>Customizable · Integration-first · AI-driven · Extensible to any extreme.</span>
        </p>
      </div>
    </section>
  );
}
